/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListMcpServerAuthClientChoicesRequest,
  ListMcpServerAuthClientChoicesSecurity,
} from "../models/operations/listmcpserverauthclientchoices.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildMcpServerAuthClientChoicesQuery,
  McpServerAuthClientChoicesQueryData,
  prefetchMcpServerAuthClientChoices,
  queryKeyMcpServerAuthClientChoices,
} from "./mcpServerAuthClientChoices.core.js";
export {
  buildMcpServerAuthClientChoicesQuery,
  type McpServerAuthClientChoicesQueryData,
  prefetchMcpServerAuthClientChoices,
  queryKeyMcpServerAuthClientChoices,
};

export type McpServerAuthClientChoicesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listMcpServerAuthClientChoices authClients
 *
 * @remarks
 * List the shared upstream credentials an MCP server offers that a credential of the caller's, such as an agent key, can be set up to use. Requires access to connect to the server. Returns each choice's id, name and client_type, and whether the upstream requires authentication; no headers or credentials.
 */
export function useMcpServerAuthClientChoices(
  request: ListMcpServerAuthClientChoicesRequest,
  security?: ListMcpServerAuthClientChoicesSecurity | undefined,
  options?: QueryHookOptions<
    McpServerAuthClientChoicesQueryData,
    McpServerAuthClientChoicesQueryError
  >,
): UseQueryResult<
  McpServerAuthClientChoicesQueryData,
  McpServerAuthClientChoicesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildMcpServerAuthClientChoicesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listMcpServerAuthClientChoices authClients
 *
 * @remarks
 * List the shared upstream credentials an MCP server offers that a credential of the caller's, such as an agent key, can be set up to use. Requires access to connect to the server. Returns each choice's id, name and client_type, and whether the upstream requires authentication; no headers or credentials.
 */
export function useMcpServerAuthClientChoicesSuspense(
  request: ListMcpServerAuthClientChoicesRequest,
  security?: ListMcpServerAuthClientChoicesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    McpServerAuthClientChoicesQueryData,
    McpServerAuthClientChoicesQueryError
  >,
): UseSuspenseQueryResult<
  McpServerAuthClientChoicesQueryData,
  McpServerAuthClientChoicesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildMcpServerAuthClientChoicesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setMcpServerAuthClientChoicesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      mcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: McpServerAuthClientChoicesQueryData,
): McpServerAuthClientChoicesQueryData | undefined {
  const key = queryKeyMcpServerAuthClientChoices(...queryKeyBase);

  return client.setQueryData<McpServerAuthClientChoicesQueryData>(key, data);
}

export function invalidateMcpServerAuthClientChoices(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      mcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "authClients",
      "listMcpServerChoices",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllMcpServerAuthClientChoices(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "authClients", "listMcpServerChoices"],
  });
}
